import {
  N_WysiwygCustomText,
  N_WysiwygDescendant,
  N_WysiwygDescendant_Paragraph,
} from "@specsheet-common/shared-types";
import { Editor, Node, Point, Text } from "slate";

const isParagraphNode = (
  node: N_WysiwygCustomText | N_WysiwygDescendant
): node is N_WysiwygDescendant_Paragraph => {
  return (node as N_WysiwygDescendant).kind === "paragraph";
};

const nonEmptyNodes = (nodes: N_WysiwygDescendant[]): N_WysiwygDescendant[] => {
  return nodes.filter((descendant) => descendant.value.length);
};

const nodesToListString = (nodes: N_WysiwygDescendant[]): string[][] => {
  return nodes.map((descendant) => {
    if (isParagraphNode(descendant)) {
      return descendant.value.map((node) => Node.string(node).trim());
    }

    return descendant.value.flatMap((node) =>
      node.value.map((n) => Node.string(n).trim())
    );
  });
};

export const labelFromRichText = (
  richText: N_WysiwygDescendant[]
): string | undefined => {
  if (richText.length) {
    const nodes = nonEmptyNodes(richText);
    return nodesToListString(nodes).flat().join(" ");
  }
  return undefined;
};

/** Plain-text offset under the cursor within `root` (Slate preview / editor DOM). */
export const getDomTextOffsetAtPoint = (
  root: HTMLElement,
  clientX: number,
  clientY: number
): number | null => {
  let range: Range | null = null;

  if (typeof document.caretRangeFromPoint === "function") {
    range = document.caretRangeFromPoint(clientX, clientY);
  } else {
    const doc = document as Document & {
      caretPositionFromPoint?(
        x: number,
        y: number
      ): { offsetNode: globalThis.Node; offset: number } | null;
    };
    const pos = doc.caretPositionFromPoint?.(clientX, clientY);
    if (pos) {
      range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
    }
  }

  if (!range || !root.contains(range.startContainer)) {
    return null;
  }

  const prefix = document.createRange();
  prefix.selectNodeContents(root);
  prefix.setEnd(range.startContainer, range.startOffset);
  return prefix.toString().length;
};

/** Map a concatenated plain-text offset onto a Slate point. */
export const findSlatePointAtTextOffset = (
  editor: Editor,
  offset: number
): Point | null => {
  let remaining = Math.max(0, offset);

  for (const [node, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    const text = (node as Text).text;
    if (remaining <= text.length) {
      return { path, offset: remaining };
    }
    remaining -= text.length;
  }

  try {
    return Editor.end(editor, []);
  } catch {
    return null;
  }
};
